import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, FreeMode } from 'swiper/modules';
import { Maximize2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import SkeletonImage from './SkeletonImage';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';

const GalleryCarousel = ({ images, title, strapiUrl }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  if (!images || !Array.isArray(images) || images.length === 0) return null;

  const getImageUrl = (img) => {
    if (!img) return "";
    const url = img.url || img;
    return url.startsWith('http') ? url : `${strapiUrl}${url}`;
  };

  return (
    <div className="w-full py-8 group relative">
      <style>{`
        .swiper-button-next, .swiper-button-prev {
          color: #16A34A !important;
          background: white;
          width: 40px !important;
          height: 40px !important;
          border-radius: 50%;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
        }
        .swiper-button-next:after, .swiper-button-prev:after {
          font-size: 18px !important;
          font-weight: bold;
        }
        .swiper-pagination-bullet-active {
          background: #16A34A !important;
        }
      `}</style>

      {title && (
        <h3 className="text-xl font-bold text-[#0a1628] mb-6 px-2 border-l-4 border-[#16A34A] pl-4">
          {title}
        </h3>
      )}
      
      <Swiper
        modules={[Navigation, Pagination, Autoplay, FreeMode]}
        spaceBetween={20}
        slidesPerView={1.2}
        navigation={true}
        pagination={{ clickable: true }}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        }}
        freeMode={true}
        breakpoints={{
          640: { slidesPerView: 2.2 },
          1024: { slidesPerView: 3.2 },
          1280: { slidesPerView: 4.2 },
        }}
        className="pb-12 !px-2"
      >
        {images.map((img, index) => {
          const url = getImageUrl(img);
          return (
            <SwiperSlide key={img.id || index} className="h-full">
              <div 
                onClick={() => setSelectedImage(url)}
                className="relative aspect-video rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 bg-gray-100 group/item cursor-zoom-in"
              >
                <SkeletonImage
                  src={url}
                  alt={img.alternativeText || `Gallery image ${index + 1}`}
                  className="w-full h-full"
                  imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover/item:scale-110"
                  overlay={
                    <div className="absolute inset-0 bg-black/0 group-hover/item:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <div className="bg-[#16A34A] p-3 rounded-full text-white opacity-0 group-hover/item:opacity-100 transform translate-y-4 group-hover/item:translate-y-0 transition-all duration-300 shadow-lg">
                        <Maximize2 size={20} />
                      </div>
                    </div>
                  }
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* Lightbox / Fullscreen Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4 md:p-10 animate-fadeIn"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white hover:text-[#16A34A] transition-colors z-50"
            onClick={() => setSelectedImage(null)}
          >
            <X size={40} />
          </button>
          
          <img
            src={selectedImage}
            alt="Fullscreen view"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-zoomIn"
          />
        </div>
      )}
    </div>
  );
};

export default GalleryCarousel;
